import type { Party } from './schemas.js';
import type { ClimbingDiscipline } from './disciplines.js';
import type { GradingSystem } from './grading-systems.js';

// The party's climbing discipline as clients need it: enough to label
// grades and highlight the right floor plan.
export interface PartyDisciplineView {
  id: string;
  discipline: ClimbingDiscipline;
  gradingSystem: GradingSystem;
  layoutImageUrl: string | null;
  layoutImageWidth: number | null;
  layoutImageHeight: number | null;
}

export type GameState = 'lobby' | 'playing' | 'ended';

export type GameModeKey =
  | 'wheel_spin'
  | 'power_up'
  | 'board'
  | 'triple_choice'
  | 'steal'
  | 'streak'
  | 'draft'
  | 'marathon'
  | 'head_to_head'
  | 'team'
  | 'expedition';

// Order here = order shown in the host's mode-picker grid. New modes
// slot in right after the classic Wheel Spin so they're prominent.
export const GAME_MODE_KEYS: readonly GameModeKey[] = [
  'wheel_spin',
  'expedition',
  'team',
  'power_up',
  'board',
  'triple_choice',
  'steal',
  'streak',
  'draft',
  'marathon',
  'head_to_head',
] as const;

export interface GameModeMeta {
  key: GameModeKey;
  label: string;
  tagline: string;
  // Single emoji used as the mode's signature glyph in headers, badges,
  // mode-picker cards, etc. Kept inside shared so client + server agree.
  emoji: string;
  // Two-color gradient stops used to theme the play view for this mode.
  // Format: [start, end] hex strings.
  accent: readonly [string, string];
}

export const GAME_MODE_META: Record<GameModeKey, GameModeMeta> = {
  wheel_spin: {
    key: 'wheel_spin',
    label: 'Wheel Spin',
    tagline: 'Classic — spin the wheel, climb what it lands on.',
    emoji: '🎡',
    accent: ['#f97a4d', '#fbbf24'],
  },
  power_up: {
    key: 'power_up',
    label: 'Power-Up',
    tagline: 'Earn & buy power-ups: free rerolls, extra hold, skip an opponent.',
    emoji: '⚡',
    accent: ['#facc15', '#a855f7'],
  },
  board: {
    key: 'board',
    label: 'Board Game',
    tagline: 'Race across a 30-tile board — crash pads, highballs, send trains, topo maps and more. First to the finish wins.',
    emoji: '🎲',
    accent: ['#3b82f6', '#22c55e'],
  },
  triple_choice: {
    key: 'triple_choice',
    label: 'Triple Choice',
    tagline: 'Three random routes — you pick which one to climb.',
    emoji: '🎯',
    accent: ['#10b981', '#34d399'],
  },
  steal: {
    key: 'steal',
    label: 'Steal-on-Bail',
    tagline: 'Bail a route and the others bid to steal it.',
    emoji: '🥷',
    accent: ['#8b5cf6', '#a855f7'],
  },
  streak: {
    key: 'streak',
    label: 'King of the Hill',
    tagline: 'Consecutive sends stack a points multiplier. One bail resets it.',
    emoji: '🔥',
    accent: ['#ef4444', '#fbbf24'],
  },
  draft: {
    key: 'draft',
    label: 'Draft',
    tagline: 'One shared route, everyone bids points to claim the climb.',
    emoji: '🔨',
    accent: ['#d97706', '#fbbf24'],
  },
  marathon: {
    key: 'marathon',
    label: 'Marathon',
    tagline: 'Beat the clock — fast turns, no modifier picking.',
    emoji: '⏱️',
    accent: ['#dc2626', '#f97316'],
  },
  head_to_head: {
    key: 'head_to_head',
    label: 'Head-to-Head',
    tagline: 'Two climbers, same route, first to send wins.',
    emoji: '⚔️',
    accent: ['#3b82f6', '#ef4444'],
  },
  team: {
    key: 'team',
    label: 'Team Bingo',
    tagline: 'Co-op! Fill a shared bingo card of route challenges — complete a line together to win.',
    emoji: '🤝',
    accent: ['#14b8a6', '#22c55e'],
  },
  expedition: {
    key: 'expedition',
    label: 'Expedition',
    tagline: 'Roguelite ascent — pick your path, manage stamina, collect relics. Summit before you run out. Solo or roped-up.',
    emoji: '🏔️',
    accent: ['#6366f1', '#22d3ee'],
  },
};

// ─── Expedition mode (roguelite ascent) ──────────────────────────────
//
// The party climbs a mountain in stages. Each turn the active climber
// picks one of a few forks (each a route at a different difficulty
// relative to THEIR grade, or a special node — camp/cache/mystery),
// then climbs it. Sends/bails move stamina; relics persist for the run
// and bend the rules; random events spice it up. Reach the summit before
// stamina hits zero. Co-op: one shared run (shared stamina/altitude/
// relics). Solo: each climber runs their own mountain in parallel.

export type ExpeditionNodeKind =
  | 'slab'      // easy route (≈ grade − 2): low risk, heals 1 on send
  | 'traverse'  // at-grade route: balanced
  | 'crux'      // hard route (≈ grade + 2): ×1.5 score, relic chance on send
  | 'camp'      // no climb — rest, heal stamina, advance
  | 'cache'     // no climb — draft 1 of 3 relics, advance
  | 'mystery';  // a random event fires, then an at-grade climb

export type ExpeditionRelicKey =
  | 'chalk_bag'    // first bail each stage costs no stamina
  | 'crash_pad'    // bails cost 1 less stamina (min 0)
  | 'second_wind'  // survive a wipe once (drop to 1 instead of 0)
  | 'send_train'   // every 3rd consecutive send heals +1
  | 'beta_book'    // beta is free (no penalty) all run
  | 'momentum'     // sends score +50%
  | 'steady_hands' // route difficulty biased 1 easier all run
  | 'summit_fever' // +10% score per altitude cleared
  | 'rope_team'    // (co-op) once per stage a bail doesn't drain the pool
  | 'pathfinder';  // see one extra fork option + one extra relic in caches

export type ExpeditionEventKey =
  | 'storm'    // this climb is a forced challenge — but worth double
  | 'windfall' // gain a random relic
  | 'updraft'  // heal +2 stamina
  | 'rockfall' // lose 1 stamina now
  | 'whiteout' // beta is locked for this climb
  | 'cairn';   // big score bonus on this climb

export interface ExpeditionRelicMeta {
  key: ExpeditionRelicKey;
  emoji: string;
  label: string;
  description: string;
  coopOnly?: boolean;
}

export const EXPEDITION_RELIC_META: Record<ExpeditionRelicKey, ExpeditionRelicMeta> = {
  chalk_bag:    { key: 'chalk_bag',    emoji: '🧗', label: 'Chalk Bag',    description: 'Your first bail each stage costs no stamina.' },
  crash_pad:    { key: 'crash_pad',    emoji: '🛡️', label: 'Crash Pad',    description: 'Bails cost 1 less stamina.' },
  second_wind:  { key: 'second_wind',  emoji: '💨', label: 'Second Wind',  description: 'The first time stamina would hit zero, hang on at 1 instead.' },
  send_train:   { key: 'send_train',   emoji: '🚂', label: 'Send Train',   description: 'Every 3rd send in a row heals +1 stamina.' },
  beta_book:    { key: 'beta_book',    emoji: '📖', label: 'Beta Book',    description: 'Watching beta is free for the rest of the run.' },
  momentum:     { key: 'momentum',     emoji: '⚡', label: 'Momentum',     description: 'Sends are worth +50% points.' },
  steady_hands: { key: 'steady_hands', emoji: '🎯', label: 'Steady Hands', description: 'Routes are picked one grade easier for the rest of the run.' },
  summit_fever: { key: 'summit_fever', emoji: '💎', label: 'Summit Fever', description: '+10% points for every camp you’ve cleared.' },
  rope_team:    { key: 'rope_team',    emoji: '🪢', label: 'Rope Team',    description: 'Once per stage a bail doesn’t drain the team’s stamina.', coopOnly: true },
  pathfinder:   { key: 'pathfinder',   emoji: '🧭', label: 'Pathfinder',   description: 'See an extra fork each turn and an extra relic in caches.' },
};

export interface ExpeditionEventMeta {
  key: ExpeditionEventKey;
  emoji: string;
  label: string;
  description: string;
}

export const EXPEDITION_EVENT_META: Record<ExpeditionEventKey, ExpeditionEventMeta> = {
  storm:    { key: 'storm',    emoji: '🌩️', label: 'Storm rolling in', description: 'This climb comes with a forced challenge — but it pays double.' },
  windfall: { key: 'windfall', emoji: '🎁', label: 'Windfall',         description: 'You found a stashed relic.' },
  updraft:  { key: 'updraft',  emoji: '🪂', label: 'Updraft',          description: 'A lucky gust — heal +2 stamina.' },
  rockfall: { key: 'rockfall', emoji: '🪨', label: 'Rockfall',         description: 'Loose rock! Lose 1 stamina.' },
  whiteout: { key: 'whiteout', emoji: '🌫️', label: 'Whiteout',         description: 'Beta is locked — read the wall yourself this climb.' },
  cairn:    { key: 'cairn',    emoji: '✨', label: 'Trail cairn',      description: 'A waymarker blesses this climb with bonus points.' },
};

export const EXPEDITION_NODE_META: Record<ExpeditionNodeKind, { emoji: string; label: string }> = {
  slab:     { emoji: '🪨', label: 'Slab' },
  traverse: { emoji: '➡️', label: 'Traverse' },
  crux:     { emoji: '🔥', label: 'Crux' },
  camp:     { emoji: '⛺', label: 'Camp' },
  cache:    { emoji: '📦', label: 'Cache' },
  mystery:  { emoji: '❓', label: 'Mystery' },
};

export interface ExpeditionRun {
  stamina: number;
  maxStamina: number;
  // Stages (nodes) cleared so far; summit when altitude >= totalStages.
  altitude: number;
  relics: ExpeditionRelicKey[];
  consecutiveSends: number;
  status: 'climbing' | 'summited' | 'wiped';
  // Per-stage transients reset when a new fork is offered.
  freeBailAvailable?: boolean; // chalk_bag / rope_team this stage
  secondWindUsed?: boolean;
  // Ember Rift: a surge vent was crossed — the run's next SEND pays
  // 1.5× (cleared on the next completed climb either way).
  ventBonus?: boolean;
}

export interface ExpeditionNodeOption {
  index: number;
  kind: ExpeditionNodeKind;
  // Climb nodes carry a concrete route; special nodes leave these null.
  routeId: string | null;
  routeDifficulty: string | null;
  routeHoldColor: string | null;
  label: string;
  detail: string;
  // Pre-rolled so a mystery card can hint at the vibe (and the server
  // resolves the same event the climber saw teased).
  eventKey?: ExpeditionEventKey;
}

export interface ExpeditionLogEntry {
  memberId: string;
  text: string;
  good: boolean;
  at: string;
}

// ── Expedition biomes ───────────────────────────────────────────────
// Selectable mountains. Each biome tunes the run's economy server-side
// (event weather, camp healing, crux payout, special-node mix) and
// reskins the client HUD. Pinned into ExpeditionState.map at start so
// mid-game settings edits can't change a running expedition.

export type ExpeditionMapKey = 'alpine' | 'ember_rift' | 'frost_couloir';

export const EXPEDITION_MAP_KEYS: readonly ExpeditionMapKey[] = [
  'alpine', 'ember_rift', 'frost_couloir',
];

export const EXPEDITION_MAP_META: Record<ExpeditionMapKey, MapMeta> = {
  alpine: {
    label: 'Alpine Ascent',
    emoji: '🏔️',
    tagline: 'The classic climb — fair weather, honest odds.',
    accent: ['#6366f1', '#22d3ee'],
  },
  ember_rift: {
    label: 'Ember Rift',
    emoji: '🌋',
    tagline: 'SURGE VENTS at marked heights shake the wall — lose stamina, then your next send pays 1.5×. Cruxes pay 1.8×.',
    accent: ['#f97a4d', '#fbbf24'],
  },
  frost_couloir: {
    label: 'Frost Couloir',
    emoji: '❄️',
    tagline: 'SHELTERED LEDGES at marked heights restore stamina on arrival — the long, cold endurance line.',
    accent: ['#38bdf8', '#e0f2fe'],
  },
};

// ── Expedition zone landmarks ───────────────────────────────────────
// Fixed altitudes on the mountain where a biome's signature mechanic
// fires when a run reaches them (Ember Rift: surge vents; Frost
// Couloir: sheltered ledges). Shared so the server can trigger them and
// the client can draw them on the mountain BEFORE anyone gets there —
// visible hazards you climb toward are what make a map feel like a
// place.
export type ExpeditionZoneKind = 'vent' | 'ledge';

export function expeditionZoneKind(map: ExpeditionMapKey | undefined): ExpeditionZoneKind | null {
  if (map === 'ember_rift') return 'vent';
  if (map === 'frost_couloir') return 'ledge';
  return null;
}

// Zone altitudes scale with the run length (host-configurable 6–15
// stages): vents at ~1/3 and ~2/3 up; ledges at ~2/5 and ~4/5. Never on
// the start or the summit.
export function expeditionZoneAltitudes(
  map: ExpeditionMapKey | undefined,
  totalStages: number,
): number[] {
  const kind = expeditionZoneKind(map);
  if (!kind) return [];
  const fracs = kind === 'vent' ? [0.34, 0.67] : [0.4, 0.8];
  const set = new Set<number>();
  for (const f of fracs) {
    const alt = Math.round(totalStages * f);
    if (alt >= 1 && alt < totalStages) set.add(alt);
  }
  return [...set].sort((a, b) => a - b);
}

export interface ExpeditionState {
  // Which biome this expedition runs on. Optional for parties that
  // pre-date maps — treat undefined as 'alpine'.
  map?: ExpeditionMapKey;
  coop: boolean;
  totalStages: number;
  // co-op: a single run keyed 'team'; solo: one run per memberId.
  runs: Record<string, ExpeditionRun>;
  // Forks awaiting the active climber's pick. Null while a climb spin is
  // in flight or between turns.
  pendingChoice: { memberId: string; runKey: string; options: ExpeditionNodeOption[] } | null;
  // Relic draft (from a cache node) awaiting a pick.
  pendingRelic: { memberId: string; runKey: string; options: ExpeditionRelicKey[] } | null;
  // Recent run events for the feed (most recent last; trimmed server-side).
  log: ExpeditionLogEntry[];
  outcome: 'in_progress' | 'summited' | 'wiped' | 'mixed' | null;
}

// The run a given member contributes to: shared 'team' in co-op, their
// own id in solo.
export function expeditionRunKey(state: Pick<ExpeditionState, 'coop'>, memberId: string): string {
  return state.coop ? 'team' : memberId;
}

// ─── Team mode (co-op "Send Bingo") ──────────────────────────────────
//
// The whole party shares one bingo card. Each cell is a route challenge
// (send a specific color, flash one, take a challenge, etc.) generated
// from the gym's real routes. A send stamps the first matching open cell
// in the climber's color; the team wins by completing any line.

export type TeamCellKind =
  | 'color'           // send a route of `arg` hold color
  | 'tag'             // send a route carrying the `arg` route-tag id
  | 'grade_up'        // send `arg` grades above your average
  | 'grade_down'      // send `arg` grades below your average
  | 'at_grade'        // send right at your average grade
  | 'flash'           // send first try
  | 'second_try'      // send on exactly the 2nd attempt
  | 'grind'           // send on the 3rd+ attempt
  | 'persist'         // send on the 5th+ attempt
  | 'challenge'       // send with a challenge modifier
  | 'flash_challenge' // flash a route that has a challenge modifier
  | 'beta'            // send after watching beta
  | 'no_beta'         // send a route that HAS beta, without watching it
  | 'chain'           // team reaches `arg` consecutive sends (no bails)
  | 'hot_streak'      // send while the team is on a 2+ send streak
  | 'rotation';       // every climber sends at least once

export interface TeamCell {
  id: string;
  kind: TeamCellKind;
  label: string;
  // color name / grade delta / chain length, depending on kind.
  arg?: string | number;
  // memberId who filled the cell (null = open). Web maps id → token color.
  filledBy: string | null;
}

export interface TeamState {
  cols: number;          // grid is cols × cols, cells row-major
  cells: TeamCell[];
  won: boolean;
  // Indexes of the cells in the winning line (for the celebration).
  winningLine: number[] | null;
  // Running co-op counters consumed by chain/rotation cells.
  chainCount: number;    // consecutive team sends; resets on any bail
  sendersSeen: string[]; // distinct memberIds who have sent at least once
  // Most recent stamp, for a "X filled <cell>" toast. `count` is how many
  // squares that send ticked off (a send can satisfy several at once).
  lastFill: { memberId: string; label: string; count: number } | null;
}

export interface GameSettings {
  challengesEnabled: boolean;
  betaEnabled: boolean;
  // 'handicap' (default): points are scored against the GAP to the
  // climber's own average grade — an at-grade send is worth the same for
  // everyone, so the best climber doesn't automatically win. 'absolute':
  // legacy scoring where harder routes are simply worth more.
  scoringMode: 'handicap' | 'absolute';
  // Rubber-banding: while a climber is in last place and trailing the
  // leader by 400+/800+ points, their sends earn ×1.25/×1.5. Keeps
  // everyone in the race without deciding it for them.
  comebackBoost: boolean;
  scaleToGrade: boolean;
  wheelSize: number;
  modifierChance: number;
  maxAttemptsBeforeForceBail: number | null;
  turnTimeLimitSec: number | null;
  // Whole-session time limit in minutes (any game mode). When set, the
  // party auto-ends at started_at + N min. Marathon mode uses
  // marathonDurationMin instead; this is for everything else.
  gameTimeLimitMin: number | null;
  marathonDurationMin: number;
  stealBidWindowSec: number;
  // Each climber's bidding starting pool for Draft mode. Without it the
  // opening round has nothing to bid with. Stored only in settings — gym
  // leaderboard math is unaffected (it sums real sends only).
  draftStartingPoints: number;
  // When true, the current climber can spend `rerollCost` points to scrap
  // the rolled route and get a fresh wheel. Useful when the wheel lands on
  // something they don't feel like climbing. Cost is deducted as a negative
  // points_awarded on a closed-out "rerolled" spin so the leaderboard is
  // authoritative.
  rerollEnabled: boolean;
  rerollCost: number;
  routeTagFilter: string[];
  // Cluster routing: bias the wheel toward routes in the same gym
  // location as the group's most recent climb, so the party doesn't
  // criss-cross the gym every turn. It's a preference, not a lock —
  // routes elsewhere still come up occasionally, so the cluster drifts
  // naturally. Only has an effect when the gym has a layout with
  // locations defined. On by default.
  clusterRouting: boolean;
  // Points on/off. When off, the session is purely for fun: no points
  // are awarded or shown anywhere, reroll is free, and the party is
  // excluded from the gym leaderboard. Only honored for the "casual"
  // modes (see POINTS_OPTIONAL_MODES) — points-driven modes (Board,
  // Power-Up, Steal, Draft, King of the Hill) always keep points on,
  // and the server clamps this to true for them.
  pointsEnabled: boolean;
  // Team mode: the shared bingo card is teamBoardSize × teamBoardSize.
  teamBoardSize: number;
  // Board mode: which map to generate (tile economy + visual theme).
  boardMap: BoardMapKey;
  // Expedition mode tuning.
  expeditionCoop: boolean;     // true = one shared roped-up run; false = solo race
  expeditionStages: number;    // nodes to clear to reach the summit
  expeditionStamina: number;   // starting (and max) stamina
  expeditionMap: ExpeditionMapKey; // which biome to climb
}

// Modes where the host may turn points off. Everything else needs a
// point economy (bidding, shop, streak multiplier, board) so points
// stay forced on.
export const POINTS_OPTIONAL_MODES: readonly GameModeKey[] = [
  'wheel_spin',
  'triple_choice',
  'marathon',
  'head_to_head',
];

export function pointsOptionalForMode(mode: GameModeKey): boolean {
  return POINTS_OPTIONAL_MODES.includes(mode);
}

// Authoritative "are points on for this party" — honors the setting only
// for modes that allow turning them off; otherwise always true.
export function pointsEnabledFor(mode: GameModeKey, settings: Pick<GameSettings, 'pointsEnabled'>): boolean {
  return pointsOptionalForMode(mode) ? settings.pointsEnabled : true;
}

export const DEFAULT_GAME_SETTINGS: GameSettings = {
  challengesEnabled: true,
  betaEnabled: true,
  scoringMode: 'handicap',
  comebackBoost: true,
  scaleToGrade: false,
  wheelSize: 6,
  modifierChance: 0.35,
  maxAttemptsBeforeForceBail: null,
  turnTimeLimitSec: null,
  gameTimeLimitMin: null,
  marathonDurationMin: 30,
  stealBidWindowSec: 15,
  draftStartingPoints: 100,
  rerollEnabled: true,
  rerollCost: 100,
  routeTagFilter: [],
  clusterRouting: true,
  pointsEnabled: true,
  teamBoardSize: 5,
  boardMap: 'summit_trail',
  expeditionCoop: true,
  expeditionStages: 10,
  expeditionStamina: 5,
  expeditionMap: 'alpine',
};

export interface PartyMemberView {
  id: string;
  displayName: string;
  avgDifficultyRank: number;
  isOnline: boolean;
  isHost: boolean;
  totalPoints: number;
  addedByMemberId: string | null;
  currentStreak: number;
  // Consumable inventory for Power-Up mode (always present; empty in
  // other modes so the client doesn't need to branch on game mode).
  powerUps: PowerUpKey[];
  // Number of upcoming turns this member will be auto-skipped (set by
  // someone using the Skip Opponent power-up against them).
  pendingSkips: number;
  // Signed-in climbers can pick a hex colour on their profile that
  // identifies them across modes (board piece, team-bingo stamp).
  // Null for guests + signed-in users who haven't chosen one — those
  // fall back to the default index-based palette.
  preferredColor: string | null;
}

// ─── Power-Up mode ───────────────────────────────────────────────────────
//
// Inventory items members earn (sending above grade) or buy (shop). Each
// power-up is a one-use consumable. New types added here must also be
// added to POWER_UP_META below + the server's earning / shop maps.
export type PowerUpKey =
  | 'free_reroll'      // Reroll the route at no points cost.
  | 'extra_hold'       // Use one off-route hold without penalty (honor
                       // system — server just marks the spin).
  | 'skip_opponent'    // Target a member; their next turn is skipped.
  | 'double_points';   // Next send earns 2× points (declared pre-send).

export interface PowerUpMeta {
  key: PowerUpKey;
  label: string;
  emoji: string;
  description: string;
  // Points required to buy in the shop. Earned drops cost nothing.
  shopCost: number;
  // True for power-ups that target another player (skip_opponent).
  targetsMember?: boolean;
}

export const POWER_UP_META: Record<PowerUpKey, PowerUpMeta> = {
  free_reroll: {
    key: 'free_reroll',
    label: 'Free Reroll',
    emoji: '🔄',
    description: 'Scrap the rolled route and respin — costs zero points.',
    shopCost: 75,
  },
  extra_hold: {
    key: 'extra_hold',
    label: 'Extra Hold',
    emoji: '✋',
    description: 'Use one hold off the route without taking a points penalty.',
    shopCost: 50,
  },
  skip_opponent: {
    key: 'skip_opponent',
    label: 'Skip Opponent',
    emoji: '⏭️',
    description: 'Pick another climber — they skip their next turn.',
    shopCost: 200,
    targetsMember: true,
  },
  double_points: {
    key: 'double_points',
    label: 'Double Points',
    emoji: '✨',
    description: 'Your next sent climb is worth double points.',
    shopCost: 150,
  },
};

// ─── Board mode ──────────────────────────────────────────────────────────

export type BoardTileType =
  | 'normal'     // No effect.
  | 'boost'      // Move forward an extra `magnitude` spaces.
  | 'trap'       // Move backward `magnitude` spaces.
  | 'star'       // Collect 1 star (track per member; tiebreaker on
                 // simultaneous finish).
  | 'swap'       // Swap positions with the player closest behind you
                 // (no-op if you're last).
  | 'reroll'     // Award a one-shot free reroll for the next spin.
  | 'shortcut'   // Jump forward `magnitude` spaces (typically 4-7) —
                 // bigger than a boost, and chains into the destination
                 // tile's effect.
  | 'lightning'  // Strike every other non-finished player back 1 tile.
  | 'mystery'    // Roll a random outcome at landing time — boost / trap
                 // / star / reroll / nothing. The rolled type is
                 // recorded in the event so the UI can replay it.
  | 'crash_pad'  // Gain a one-shot trap shield. Next trap (direct or
                 // via mystery) is negated and the shield is consumed.
  | 'rest_day'   // Skip your next turn — but you bank 2 stars while
                 // you're "resting". Implemented via party_members
                 // pending_skips + a stars bump.
  | 'highball'   // Risky tile: 50/50 gamble. +5 spaces on heads,
                 // −3 on tails. Like a stronger, riskier mystery.
  | 'topo'       // Read the topo: teleport forward to the next star
                 // tile ahead of you (or to the finish if none). The
                 // destination tile fires its effect.
  | 'send_train' // Momentum bonus: if your previous completed climb
                 // was a send, advance +2 extra spaces. Otherwise no
                 // effect. Rewards consistency.
  // ── Next-turn effect tiles ───────────────────────────────────────
  // These tiles don't move the climber — they stash a NextTurnEffect
  // in BoardState.pendingModifiers[memberId] that fires when the
  // climber's next spin is initialized.
  | 'forced_challenge'   // Their next spin always rolls a challenge.
  | 'crank_it_up'        // Wheel only shows routes >= avg + 1.
  | 'cruiser_mode'       // Wheel only shows routes <= avg - 1.
  | 'color_lock'         // Wheel only shows one random hold color.
  | 'double_or_nothing'  // Movement is doubled (send AND bail).
  | 'no_beta'            // Beta video is blocked on the next route.
  ;

// ─── Next-turn effects ───────────────────────────────────────────────
//
// Discriminated union for the bonus/penalty a board tile imposes on
// the climber's NEXT spin. Stored in BoardState.pendingModifiers,
// drained by boardMode.initSpin into the spin's mode_data.
// `kind` mirrors the tile type that produced it for readability.

export type NextTurnEffect =
  | { kind: 'forced_challenge' }
  | { kind: 'crank_it_up' }
  | { kind: 'cruiser_mode' }
  | { kind: 'color_lock'; holdColor: string }
  | { kind: 'double_or_nothing' }
  | { kind: 'no_beta' };

export interface NextTurnEffectMeta {
  label: string;
  emoji: string;
  description: string;
  // CSS color used as the badge accent. Falls back to --accent.
  accent: string;
}

// Static metadata for each effect kind. Used by the UI banner so we
// don't repeat copy across client + server.
export const NEXT_TURN_EFFECT_META: Record<NextTurnEffect['kind'], NextTurnEffectMeta> = {
  forced_challenge: {
    label: 'Forced Challenge',
    emoji: '🎯',
    description: 'Your next route comes with a challenge modifier — no escape.',
    accent: '#f97a4d',
  },
  crank_it_up: {
    label: 'Crank It Up',
    emoji: '📈',
    description: 'Your next wheel only shows routes above your average grade.',
    accent: '#ef4444',
  },
  cruiser_mode: {
    label: 'Cruiser Mode',
    emoji: '😎',
    description: 'Your next wheel only shows routes below your average grade.',
    accent: '#22d3ee',
  },
  color_lock: {
    label: 'Color Lock',
    emoji: '🎨',
    description: 'Your next wheel only shows one randomly-chosen hold color.',
    accent: '#a855f7',
  },
  double_or_nothing: {
    label: 'Double or Nothing',
    emoji: '🎰',
    description: 'Board movement doubles next turn — sends and bails alike.',
    accent: '#fbbf24',
  },
  no_beta: {
    label: 'No Beta',
    emoji: '🚫',
    description: 'Beta video is locked for your next climb. Read the wall yourself.',
    accent: '#94a3b8',
  },
};

// ── Board maps ──────────────────────────────────────────────────────
// Mario-Party-style selectable boards. Each map is a different tile
// economy (weights, magnitudes, length) AND a different visual theme.
// The key is stored in GameSettings (host's pick) and pinned into
// BoardState.map at generation time so mid-game settings edits can't
// reskin or re-balance a running board.

export type BoardMapKey = 'summit_trail' | 'crux_cavern' | 'crystal_gallery' | 'sky_ridge';

export const BOARD_MAP_KEYS: readonly BoardMapKey[] = [
  'summit_trail', 'crux_cavern', 'crystal_gallery', 'sky_ridge',
];

export interface MapMeta {
  label: string;
  emoji: string;
  tagline: string;
  accent: [string, string];
}

export const BOARD_MAP_META: Record<BoardMapKey, MapMeta> = {
  summit_trail: {
    label: 'Summit Trail',
    emoji: '⛰️',
    tagline: 'The classic race — no tricks, just the climb.',
    accent: ['#4ade80', '#fbbf24'],
  },
  crux_cavern: {
    label: 'Crux Cavern',
    emoji: '🌋',
    tagline: 'The volcano ERUPTS every 5 climbs — tremors, ash clouds, magma vents. Chaos on a timer.',
    accent: ['#f97a4d', '#ef4444'],
  },
  crystal_gallery: {
    label: 'Crystal Gallery',
    emoji: '❄️',
    tagline: 'Ice slides you an extra tile — and passing a rival STEALS one of their shards.',
    accent: ['#22d3ee', '#a5f3fc'],
  },
  sky_ridge: {
    label: 'Sky Ridge',
    emoji: '☁️',
    tagline: 'Shifting winds speed or slow every send — and checkpoint flags pay +2⭐ to whoever claims them first.',
    accent: ['#818cf8', '#c4b5fd'],
  },
};

// ── Board map mechanics (the "sub-gamemode" layer) ─────────────────
// Each non-classic map carries a RULE, not just a tile mix. State for
// those rules lives in BoardState.mapState; things that happened during
// a move are surfaced as BoardMapEvent entries on lastMove.mapEvents so
// every client can narrate/animate them.

export type BoardWind = 'tailwind' | 'headwind' | 'calm';

export interface BoardMapState {
  // crux_cavern: climbs remaining until the next eruption. Seeded to
  // ERUPTION_EVERY at init; undefined (pre-mechanic parties) re-seeds
  // on first use.
  climbsUntilEruption?: number;
  // sky_ridge: the wind that applies to the NEXT climb (posted in
  // advance so players can plan), re-rolled after every climb.
  wind?: BoardWind;
  // sky_ridge: checkpoint tile index → memberId who claimed it.
  checkpointClaims?: Record<number, string>;
}

export type BoardMapEventKind =
  | 'eruption_tremor'   // everyone slides back
  | 'eruption_ash'      // beta locked for everyone's next climb
  | 'eruption_magma'    // everyone behind the leader boosted
  | 'eruption_rain'     // stars for everyone on special tiles
  | 'ice_slide'         // crystal_gallery: slid an extra tile
  | 'shard_steal'       // crystal_gallery: stole a star by passing
  | 'wind_shift'        // sky_ridge: wind changed
  | 'checkpoint_claim'; // sky_ridge: flag claimed (+2 stars)

export interface BoardMapEvent {
  kind: BoardMapEventKind;
  // Pre-composed, human-readable line — clients render it verbatim in
  // the move banner (and big-overlay eruptions).
  text: string;
}

export const ERUPTION_EVERY = 5;

// Sky Ridge checkpoint tiles live in board-layout.ts
// (boardCheckpointIndexes) — they're picked along the main path of the
// authored layout rather than by raw index fraction.

// Wind effect on a send's spaces (sky_ridge only). Clamped by callers.
export function windDelta(wind: BoardWind | undefined): number {
  return wind === 'tailwind' ? 1 : wind === 'headwind' ? -1 : 0;
}

export interface BoardTile {
  type: BoardTileType;
  // Set for boost/trap (magnitude moved) and shortcut (forward jump).
  magnitude?: number;
}

export interface BoardTileEvent {
  tile: number;
  type: BoardTileType;
  // For boost/trap/shortcut/topo/highball/send_train (when triggered),
  // the new position after the tile fired.
  toTile?: number;
  // For swap.
  swappedWithMemberId?: string;
  // For lightning: which non-finished members got pushed back.
  affectedMemberIds?: string[];
  // For mystery: which sub-effect actually rolled. Mirrors the BoardTileType
  // taxonomy minus 'mystery' itself so we don't recurse.
  rolledType?: Exclude<BoardTileType, 'mystery'>;
  // For mystery → boost/trap: how many spaces the roll moved.
  rolledMagnitude?: number;
  // For highball: which side of the gamble came up.
  highballOutcome?: 'high' | 'low';
  // For trap (direct or rolled via mystery): true when a crash_pad
  // shield was consumed and the trap did nothing.
  shielded?: boolean;
  // For send_train: whether the bonus actually triggered (previous
  // climb was a send). False means the tile fizzled.
  trainTriggered?: boolean;
  // For next-turn effect tiles (forced_challenge, crank_it_up,
  // cruiser_mode, color_lock, double_or_nothing, no_beta) — what
  // effect got stashed in pendingModifiers. The UI uses this to
  // describe the tile in the last-move banner.
  nextTurnEffect?: NextTurnEffect;
}

export interface BoardLastMove {
  memberId: string;
  fromTile: number;
  toTile: number;
  // Number of base spaces moved (before tile chain). 0 for bails.
  spaces: number;
  // Tile effects triggered in order (may chain — boost-onto-boost etc.).
  events: BoardTileEvent[];
  // Map-mechanic happenings this move (eruptions, ice slides, shard
  // steals, wind shifts, checkpoint claims). Absent on classic maps and
  // on moves where nothing map-specific fired.
  mapEvents?: BoardMapEvent[];
  appliedAt: string;
}

export interface BoardState {
  // Which map this board was generated on. Optional for parties that
  // pre-date the map system — treat undefined as 'summit_trail'.
  map?: BoardMapKey;
  tiles: BoardTile[];
  // memberId → tile index. 0 = start, tiles.length - 1 = finish.
  positions: Record<string, number>;
  // memberId → star count.
  stars: Record<string, number>;
  // Member ids in the order they crossed the finish line.
  finished: string[];
  // memberId → remaining crash-pad shields. Each shield negates one
  // trap (direct or via mystery roll) before being consumed. Optional
  // for back-compat with existing parties created before crash_pad
  // existed; treat undefined as the empty map.
  shields?: Record<string, number>;
  // memberId → next-turn effect waiting to fire. Set when the climber
  // lands on a next-turn modifier tile; drained by boardMode.initSpin
  // and stashed in the new spin's mode_data.activeBoardEffect. One
  // pending effect per member at a time; landing on a second
  // overwrites the first.
  pendingModifiers?: Record<string, NextTurnEffect>;
  // Per-map mechanic state (eruption clock, wind, checkpoint claims).
  // Absent on classic maps and on parties from before map mechanics.
  mapState?: BoardMapState;
  lastMove: BoardLastMove | null;
}

export interface RouteSummary {
  id: string;
  name: string | null;
  difficulty: string;
  difficultyRank: number;
  holdColor: string;
  photoUrls: string[];
  // Parallel-by-index with photoUrls. Empty string at an index means
  // "no processed version for this photo, fall back to the original".
  processedPhotoUrls: string[];
  betaVideoUrl: string | null;
  // Gym-layout location this route is tagged with (null when unset or
  // the gym has no layout). The in-game view renders the highlighted
  // floor-plan preview from this + the gym's layout image.
  locationId: string | null;
}

export interface RouteWheelOption {
  id: string;
  name: string | null;
  difficulty: string;
  holdColor: string;
}

export interface ModifierSummary {
  id: string;
  name: string;
  multiplier: number;
}

export type SpinPhase =
  | 'spinning'
  | 'picking_route'
  | 'picking_modifier'
  | 'climbing'
  | 'steal_bidding'
  | 'steal_climbing'
  | 'h2h_ready'
  | 'completed';

export interface StealBid {
  memberId: string;
  displayName: string;
  amount: number;
}

export interface SpinView {
  id: string;
  partyMemberId: string;
  route: RouteSummary | null;
  wheelOptions: RouteWheelOption[];
  winningIndex: number | null;
  modifierOptions: ModifierSummary[];
  selectedModifierId: string | null;
  phase: SpinPhase;
  outcome: 'sent' | 'bailed' | 'skipped' | null;
  betaViewed: boolean;
  pointsAwarded: number | null;
  betaPenalty: number;
  attemptCount: number;
  // ISO deadline after which the climber's spin auto-bails, derived from
  // the spin's created_at + settings.turnTimeLimitSec. null when no
  // turn time limit is set for the session.
  turnDeadline: string | null;
  // Rubber-banding multiplier currently in effect for this climber
  // (1 when none). Server recomputes at completion; this drives the
  // client's projected-points display + comeback badge.
  comebackMultiplier: number;
  routeChoices: RouteSummary[];
  stealBids: StealBid[];
  stealDeadline: string | null;
  stealWinnerMemberId: string | null;
  stealBidAmount: number | null;
  streakCount: number;
  h2hOpponentMemberId: string | null;
  h2hReadyMemberIds: string[];
  // Power-Up mode: declared before completing the climb. Server reads
  // these to apply effects (Double Points multiplies the awarded points,
  // Extra Hold is a self-policed marker for the gym).
  doublePointsActive: boolean;
  extraHoldUsed: boolean;
  // Board mode: the next-turn effect that was drained from
  // BoardState.pendingModifiers when this spin was initialized. Drives
  // the animated "modifier active" banner above the wheel + any
  // per-spin server effects (double_or_nothing's movement multiplier,
  // no_beta's blocked beta button).
  activeBoardEffect: NextTurnEffect | null;
}

export interface PartyStateView {
  party: Pick<Party, 'id' | 'name' | 'joinCode' | 'status'> & {
    gameState: GameState;
    currentClimberId: string | null;
    betaPenalty: number;
    submitToLeaderboard: boolean;
    gameMode: GameModeKey;
    gameSettings: GameSettings;
    marathonEndsAt: string | null;
    // Null unless gameMode === 'board'. Generated at start_game,
    // mutated on each climb completion.
    boardState: BoardState | null;
    // Null unless gameMode === 'team'. The shared bingo card.
    teamState: TeamState | null;
    // Null unless gameMode === 'expedition'. The roguelite run state.
    expeditionState: ExpeditionState | null;
    // The climbing discipline this session is scoped to, resolved from
    // the party's gym_disciplines row. Null for parties created before
    // multi-discipline support, which draw from the gym's whole pool.
    // Carries the grade scale so clients label difficulties correctly
    // without a second fetch.
    discipline: PartyDisciplineView | null;
  };
  members: PartyMemberView[];
  currentSpin: SpinView | null;
  leaderboard: { partyMemberId: string; displayName: string; points: number }[];
}

export interface PartySummaryMember {
  partyMemberId: string;
  displayName: string;
  points: number;
  sends: number;
  bails: number;
  challengesAccepted: number;
  hardestSendDifficulty: string | null;
  hardestSendRank: number;
  // Board mode standings (null in other modes). finishRank is 1-based
  // in the order players crossed the line (1 = first to finish), or
  // null if they never finished. tile = furthest tile reached;
  // stars = stars collected.
  boardFinishRank: number | null;
  boardTile: number | null;
  boardStars: number | null;
  // Expedition standings (null in other modes). altitude = stages
  // cleared; summited = reached the top with stamina to spare.
  expeditionAltitude: number | null;
  expeditionSummited: boolean | null;
  expeditionStamina: number | null;
}

export interface PartySummarySuperlative {
  partyMemberId: string;
  displayName: string;
  value: number | string;
}

export interface GymLeaderboardClimber {
  key: string;
  displayName: string;
  points: number;
  sends: number;
  bails: number;
  challenges: number;
  hardestDifficulty: string | null;
  hardestRank: number;
  hardestHoldColor: string | null;
}

export interface GymLeaderboardActivity {
  id: string;
  createdAt: string;
  climberName: string;
  routeDifficulty: string | null;
  routeName: string | null;
  holdColor: string | null;
  points: number;
  outcome: 'sent' | 'bailed' | 'skipped';
  hadModifier: boolean;
}

export interface GymLeaderboardEligibility {
  // Total parties for this gym (any status).
  total: number;
  // Parties whose host ticked "submit to leaderboard".
  optedIn: number;
  // Opted-in parties rejected because their host's coords are missing.
  missingCoords: number;
  // Opted-in parties rejected because their host's coords are outside the geofence.
  outOfRange: number;
  // Opted-in parties that passed every check and feed the leaderboard.
  eligible: number;
  geofenceConfigured: boolean;
  geofenceRadiusM: number;
}

export interface GymDailyLeaderboardView {
  gym: { id: string; slug: string; name: string; timezone: string } | null;
  // Gym-local calendar day the board covers, YYYY-MM-DD.
  date: string;
  // Server clock when this snapshot was built.
  generatedAt?: string;
  totals: {
    climbers: number;
    sends: number;
    bails: number;
    challenges: number;
    totalPoints: number;
    activeParties: number;
  };
  leaderboard: GymLeaderboardClimber[];
  superlatives: {
    mostPoints: GymLeaderboardClimber | null;
    mostSends: GymLeaderboardClimber | null;
    mostChallenges: GymLeaderboardClimber | null;
    hardestSend: GymLeaderboardClimber | null;
  };
  activity: GymLeaderboardActivity[];
  eligibility?: GymLeaderboardEligibility;
}

export interface PartySummaryView {
  party: {
    id: string;
    name: string;
    status: string;
    startedAt: string | null;
    endedAt: string | null;
    gymSlug: string;
    gymName: string;
    // Drives which summary view + podium ranking to render. Board mode
    // ranks by finish order instead of points.
    gameMode: GameModeKey;
    // When false (points disabled for the session), the summary hides
    // point totals and ranks by sends instead.
    pointsEnabled: boolean;
    // Team mode: whether the team completed the bingo card.
    teamWon?: boolean;
    // Team mode: the final shared bingo card (for the co-op reveal).
    teamState?: TeamState | null;
    // Expedition mode: the final run state (for the summit/wipe reveal).
    expeditionState?: ExpeditionState | null;
  };
  leaderboard: PartySummaryMember[];
  totals: {
    spins: number;
    sends: number;
    bails: number;
    challengesAccepted: number;
  };
  superlatives: {
    mostSends: PartySummarySuperlative | null;
    mostChallenges: PartySummarySuperlative | null;
    hardestSend: (PartySummarySuperlative & { routeName: string | null; holdColor: string | null }) | null;
    mostPoints: PartySummarySuperlative | null;
  };
}

export interface ServerToClientEvents {
  'party:me': (payload: { memberId: string }) => void;
  'party:state': (state: PartyStateView) => void;
  'party:member_joined': (member: PartyMemberView) => void;
  'party:member_left': (memberId: string) => void;
  'party:game_started': (payload: { currentClimberId: string }) => void;
  'party:turn_assigned': (payload: { currentClimberId: string }) => void;
  'party:spin_started': (spin: SpinView) => void;
  'party:modifier_picked': (spin: SpinView) => void;
  'party:beta_revealed': (payload: { spinId: string }) => void;
  'party:spin_completed': (spin: SpinView) => void;
  'party:leaderboard_updated': (leaderboard: PartyStateView['leaderboard']) => void;
  'party:settings_updated': (payload: { gameMode: GameModeKey; settings: GameSettings }) => void;
  'party:steal_window_opened': (spin: SpinView) => void;
  'party:steal_bid_placed': (spin: SpinView) => void;
  'party:steal_resolved': (spin: SpinView) => void;
  'party:marathon_tick': (payload: { secondsLeft: number }) => void;
  // Fired when a climber's spin row marks completed (sent/bailed/skipped).
  // Drives the between-round leaderboard overlay on the client. Carries the
  // minimum needed for the animation — display name avoids a race with
  // member-rename events; pointsAwarded is what was *just* added/subtracted.
  'party:round_resolved': (payload: {
    climberMemberId: string;
    climberName: string;
    outcome: 'sent' | 'bailed' | 'skipped';
    pointsAwarded: number;
  }) => void;
  // Power-Up: emitted when a member earns a drop from sending above
  // their grade. Client uses this for the toast / inventory bump.
  'party:power_up_earned': (payload: {
    memberId: string;
    memberName: string;
    powerUp: PowerUpKey;
    reason: 'climb' | 'tile';
  }) => void;
  'party:power_up_used': (payload: {
    memberId: string;
    memberName: string;
    powerUp: PowerUpKey;
    targetMemberId?: string;
    targetMemberName?: string;
  }) => void;
  // Board mode: emitted after a climb-driven move resolves so clients
  // can animate the token. The same state ends up on the next
  // party:state broadcast — this is just a hint to drive the animation.
  'party:board_move': (payload: { state: BoardState }) => void;
  // Team mode: a hint that the shared bingo card changed. The full state
  // arrives on the next party:state broadcast; this just lets the client
  // animate the new stamp promptly.
  'party:team_update': (payload: Record<string, never>) => void;
  // Expedition mode: a hint that the run state changed (path chosen,
  // node resolved, relic drafted, event fired). Carries the newest log
  // line so the client can toast it without diffing state.
  'party:expedition_update': (payload: { entry: ExpeditionLogEntry | null }) => void;
  // Board mode: the active climber dismissed the "here's what your tile
  // did" modal. Relayed to every device in the party so the modal
  // closes for everyone at once (not just the climber who tapped it).
  'party:board_event_dismissed': () => void;
  // `endedAt` is an ISO timestamp that every connected client uses as the
  // shared anchor for the podium-reveal animation. Without it, each
  // client's setTimeout chain starts when they arrive on the summary
  // page (a few hundred ms apart on slow links), so the host's screen
  // could be on "🥈 Silver locked in" while a guest is still on
  // "Calculating final standings…". With it, all clients converge.
  'party:ended': (payload?: { endedAt: string }) => void;
  'error': (message: string) => void;
}

export interface ClientToServerEvents {
  'party:start_game': (
    input: {
      submitToLeaderboard: boolean;
      latitude: number | null;
      longitude: number | null;
    },
    ack: (res: { ok: true } | { ok: false; error: string }) => void,
  ) => void;
  'party:add_member': (
    input: { displayName: string; avgDifficultyRank: number },
    ack: (res: { ok: true; memberId: string } | { ok: false; error: string }) => void,
  ) => void;
  'party:rename_member': (
    input: { memberId: string; displayName: string },
    ack: (res: { ok: true } | { ok: false; error: string }) => void,
  ) => void;
  'party:update_avg_grade': (
    input: { memberId: string; avgDifficultyRank: number },
    ack: (res: { ok: true } | { ok: false; error: string }) => void,
  ) => void;
  'party:spin_wheel': (
    ack: (res: { ok: true; spin: SpinView } | { ok: false; error: string }) => void,
  ) => void;
  'party:pick_modifier': (
    input: { spinId: string; modifierId: string | null },
    ack: (res: { ok: true; spin: SpinView } | { ok: false; error: string }) => void,
  ) => void;
  'party:reveal_beta': (
    input: { spinId: string },
    ack: (res: { ok: true } | { ok: false; error: string }) => void,
  ) => void;
  'party:log_attempt': (
    input: { spinId: string },
    ack: (res: { ok: true; attemptCount: number } | { ok: false; error: string }) => void,
  ) => void;
  'party:complete_climb': (
    input: {
      spinId: string;
      outcome: 'sent' | 'bailed';
      // Board mode: which route to take if this move crosses the map's
      // fork. Pre-declared on the climb card; defaults to 'main'.
      branchChoice?: 'main' | 'branch';
    },
    ack: (res: { ok: true; points: number } | { ok: false; error: string }) => void,
  ) => void;
  'party:skip_turn': (
    input: { memberId: string },
    ack: (res: { ok: true } | { ok: false; error: string }) => void,
  ) => void;
  // Spend `rerollCost` points to scrap the current spin and roll a fresh
  // one. Caller must be the current climber (or a controller of them).
  // Server validates the setting is enabled, the climber has enough
  // points, and the spin is in a rerollable state (route selected, not
  // already completed).
  'party:reroll_route': (
    input: { spinId: string },
    ack: (res: { ok: true; cost: number } | { ok: false; error: string }) => void,
  ) => void;
  'party:end': (ack: (res: { ok: boolean }) => void) => void;
  'party:update_settings': (
    input: { gameMode?: GameModeKey; settings?: Partial<GameSettings> },
    ack: (res: { ok: true } | { ok: false; error: string }) => void,
  ) => void;
  'party:select_route': (
    input: { spinId: string; routeId: string },
    ack: (res: { ok: true; spin: SpinView } | { ok: false; error: string }) => void,
  ) => void;
  'party:place_steal_bid': (
    // memberId is optional: defaults to the caller. Lets the host bid for a
    // proxy climber controlled from the same device (server validates via
    // canControlMember).
    input: { spinId: string; amount: number; memberId?: string },
    ack: (res: { ok: true } | { ok: false; error: string }) => void,
  ) => void;
  'party:h2h_ready': (
    // memberId is optional: defaults to the caller. Used to ready up a proxy
    // climber controlled from the caller's device (server validates control).
    input: { spinId: string; memberId?: string },
    ack: (res: { ok: true } | { ok: false; error: string }) => void,
  ) => void;
  // Power-Up: buy a power-up from the shop using points. Server deducts
  // shopCost and appends to the buyer's inventory.
  'party:buy_power_up': (
    input: { powerUp: PowerUpKey },
    ack: (res: { ok: true } | { ok: false; error: string }) => void,
  ) => void;
  // Power-Up: consume one item from the inventory. `targetMemberId` is
  // required for power-ups whose meta has targetsMember = true. Spin id
  // optional and only consulted when the effect attaches to the current
  // open spin (e.g. double_points, extra_hold).
  'party:use_power_up': (
    input: { powerUp: PowerUpKey; targetMemberId?: string; spinId?: string },
    ack: (res: { ok: true } | { ok: false; error: string }) => void,
  ) => void;
  // Board mode: the active climber dismissed the tile-result modal.
  // Server relays party:board_event_dismissed to the whole room so
  // every connected device closes the modal in sync.
  'party:ack_board_event': (
    ack: (res: { ok: true } | { ok: false; error: string }) => void,
  ) => void;
  // Expedition: the active climber (or their controller) picks a fork.
  // For climb nodes this inserts the spin; camp/cache/mystery resolve
  // immediately. memberId optional → defaults to caller (proxy support).
  'party:expedition_choose': (
    input: { nodeIndex: number; memberId?: string },
    ack: (res: { ok: true } | { ok: false; error: string }) => void,
  ) => void;
  // Expedition: draft one relic from a cache node's offered options.
  'party:expedition_pick_relic': (
    input: { relicKey: ExpeditionRelicKey; memberId?: string },
    ack: (res: { ok: true } | { ok: false; error: string }) => void,
  ) => void;
}

export interface SocketHandshakeAuth {
  partyId: string;
  token: string;
  tokenKind: 'supabase' | 'guest';
}
